<template>
  <div class="admin-page ad-analytics" v-loading="loading">
    <!-- ============ 页头 ============ -->
    <div class="page-head">
      <div class="head-text">
        <h1>广告分析</h1>
        <p>
          {{ scopeLabel }} · 近 {{ query.days }} 天
          <span class="only-super"><i class="el-icon-lock" />仅超管可见</span>
        </p>
      </div>
      <div class="head-actions">
        <el-select
          v-model="query.schoolId"
          size="small"
          class="scope-select"
          @change="onSchoolFilterChange"
        >
          <el-option label="全部学校" value="" />
          <el-option
            v-for="item in schoolOptions"
            :key="String(item.id)"
            :label="item.name"
            :value="item.id"
          />
        </el-select>
        <div class="status-tabs">
          <button
            v-for="d in [7, 30, 90]"
            :key="d"
            type="button"
            class="tab"
            :class="{ 'is-active': query.days === d }"
            @click="switchDays(d)"
          >
            近 {{ d }} 天
          </button>
        </div>
      </div>
    </div>

    <!-- ============ 视觉重心：深色主指标区（本屏唯一深色块） ============ -->
    <div class="hero">
      <div class="hero__item" v-for="(item, index) in primaryMetrics" :key="item.label">
        <span class="hero__index">{{ String(index + 1).padStart(2, '0') }}</span>
        <div class="hero__label">{{ item.label }}</div>
        <div class="hero__value">
          {{ item.value }}<span class="hero__unit" v-if="item.unit">{{ item.unit }}</span>
        </div>
        <div class="hero__sub" :class="item.accent ? 'is-accent' : ''">{{ item.sub }}</div>
      </div>
    </div>

    <!-- ============ 趋势 ============ -->
    <div class="ad-card">
      <div class="ad-card__head">
        <div class="ad-card__title">观看趋势</div>
        <div class="ad-legend">
          <span><i style="background: #cfc6d1" />展示</span>
          <span><i style="background: #c93b57" />完播</span>
          <span><i style="background: #ff8a65" />未看完</span>
        </div>
      </div>
      <div ref="trendChart" class="ad-chart" />
    </div>

    <!-- ============ 分场景 + 异常 ============ -->
    <div class="ad-cols">
      <div class="ad-card">
        <div class="ad-card__head">
          <div class="ad-card__title">分场景</div>
          <span class="ad-card__hint">取出 vs 投放</span>
        </div>
        <div v-if="!byScene.length" class="ad-empty">
          <i class="el-icon-video-camera" />
          <p>近 {{ query.days }} 天没有观看记录</p>
        </div>
        <div v-else class="scene-list">
          <div class="scene" v-for="item in byScene" :key="item.scene">
            <div class="scene__head">
              <span class="scene__name">{{ item.sceneLabel }}</span>
              <span class="scene__rate">{{ percentText(item.completeRate) }}</span>
            </div>
            <div class="scene__bar">
              <div class="scene__bar-in" :style="{ width: barWidth(item.completeRate) }" />
            </div>
            <div class="scene__foot">
              <span>展示 {{ num(item.impression) }}</span>
              <span>完播 {{ num(item.complete) }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="ad-card">
        <div class="ad-card__head">
          <div class="ad-card__title">异常排查</div>
          <span class="ad-card__hint">
            加载失败 {{ num(overview.loadFailed) }} · 超时 {{ num(overview.expired) }}
          </span>
        </div>
        <div v-if="!errorTop.length" class="ad-empty">
          <i class="el-icon-circle-check" />
          <p>没有加载失败记录</p>
        </div>
        <div v-else class="err-list">
          <div class="err" v-for="item in errorTop" :key="String(item.errCode)">
            <span class="err__code">{{ item.errCode }}</span>
            <span class="err__msg">{{ item.errMsg || '未知错误' }}</span>
            <span class="err__cnt">{{ num(item.count) }}</span>
          </div>
          <p class="err__tip">
            1004 / 1005 属于广告库存与审核状态，去流量主后台看；其余多半是广告位配置或客户端问题。
          </p>
        </div>
      </div>
    </div>

    <!-- ============ 分校汇总（L2 入口） ============ -->
    <div class="ad-card">
      <div class="ad-card__head">
        <div class="ad-card__title">各学校完播</div>
        <span class="ad-card__hint">共 {{ schoolTotal }} 所有数据 · 点行下钻到用户</span>
      </div>
      <div v-if="!schools.length" class="ad-empty">
        <i class="el-icon-office-building" />
        <p>近 {{ query.days }} 天没有学校产生观看数据</p>
      </div>
      <table v-else class="ad-table">
        <thead>
          <tr>
            <th>学校</th>
            <th>展示</th>
            <th>完播</th>
            <th>未看完</th>
            <th>完播率</th>
            <th>用户数</th>
            <th>异常</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="item in schools"
            :key="String(item.schoolId)"
            class="is-clickable"
            :class="{ 'is-active': picked && picked.id === item.schoolId }"
            @click="pickSchool(item)"
          >
            <td>
              <span class="cell-name">{{ item.schoolName }}</span>
            </td>
            <td class="num">{{ num(item.impression) }}</td>
            <td class="num">{{ num(item.complete) }}</td>
            <td class="num">{{ num(item.closedEarly) }}</td>
            <td>
              <div class="rate-bar">
                <div class="rate-bar__track">
                  <div class="rate-bar__in" :style="{ width: barWidth(item.completeRate) }" />
                </div>
                <span class="rate-bar__text">{{ percentText(item.completeRate) }}</span>
              </div>
            </td>
            <td class="num">{{ num(item.userCount) }}</td>
            <td class="num" :class="{ 'is-warn': item.abnormal > 0 }">{{ num(item.abnormal) }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- ============ 校内用户明细（L3 入口） ============ -->
    <div class="ad-card" v-if="picked">
      <div class="ad-card__head">
        <div class="ad-card__title breadcrumb">
          <span class="breadcrumb__up" @click="backToSchools">← 全部学校</span>
          <span class="breadcrumb__sep">›</span>
          <span>{{ picked.name }}</span>
          <span class="breadcrumb__sep">›</span>
          <span class="breadcrumb__cur">用户明细</span>
        </div>
        <span class="ad-card__hint">共 {{ userTotal }} 名用户有过观看</span>
      </div>

      <div class="ad-toolbar">
        <el-input
          v-model="userQuery.keyword"
          size="small"
          class="search-input"
          placeholder="搜昵称或用户 ID"
          clearable
          prefix-icon="el-icon-search"
          @keyup.enter.native="searchUsers"
          @clear="searchUsers"
        />
        <div class="status-tabs">
          <button
            v-for="opt in sortOptions"
            :key="opt.value"
            type="button"
            class="tab"
            :class="{ 'is-active': userQuery.sort === opt.value }"
            @click="switchSort(opt.value)"
          >
            {{ opt.label }}
          </button>
        </div>
      </div>

      <div v-if="!users.length" class="ad-empty">
        <i class="el-icon-user" />
        <p>{{ userQuery.keyword ? '没有匹配的用户' : '近 ' + query.days + ' 天该校没有用户观看广告' }}</p>
      </div>
      <template v-else>
        <table class="ad-table">
          <thead>
            <tr>
              <th>用户</th>
              <th>展示</th>
              <th>完播</th>
              <th>未看完</th>
              <th>完播率</th>
              <th>可疑</th>
              <th>最后观看</th>
              <th>最近 IP</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="item in users"
              :key="String(item.userId)"
              class="is-clickable"
              @click="openEvents(item)"
            >
              <td>
                <div class="user-cell">
                  <span class="user-cell__avatar">{{ avatarText(item) }}</span>
                  <span class="user-cell__text">
                    <b>{{ item.nickname || '未设置昵称' }}</b>
                    <em>#{{ item.userId }}</em>
                  </span>
                </div>
              </td>
              <td class="num">{{ num(item.impression) }}</td>
              <td class="num">{{ num(item.complete) }}</td>
              <td class="num">{{ num(item.closedEarly) }}</td>
              <td>
                <div class="rate-bar">
                  <div class="rate-bar__track">
                    <div
                      class="rate-bar__in"
                      :class="{ 'is-low': item.completeRate < 0.5 }"
                      :style="{ width: barWidth(item.completeRate) }"
                    />
                  </div>
                  <span class="rate-bar__text">{{ percentText(item.completeRate) }}</span>
                </div>
              </td>
              <td class="num" :class="{ 'is-warn': item.suspect > 0 }">{{ num(item.suspect) }}</td>
              <td class="num time">{{ formatTime(item.lastWatchAt) }}</td>
              <td class="num time">{{ item.lastIp || '—' }}</td>
            </tr>
          </tbody>
        </table>

        <div class="ad-pager">
          <span>共 {{ userTotal }} 条</span>
          <el-pagination
            background
            small
            layout="prev, pager, next"
            :current-page="userQuery.page"
            :page-size="userQuery.size"
            :total="userTotal"
            @current-change="changeUserPage"
          />
        </div>
      </template>

      <p class="ad-tip">
        <b>可疑</b> 指客户端回报「已看完」但时长不到门槛的次数。单独一列、不并进「未看完」——
        一个人几十次观看里大半是可疑，那是刷奖特征，混进去就看不出来了。点用户行可看每一次观看。
      </p>
    </div>

    <!-- ============ L3：单次观看明细 ============ -->
    <el-drawer
      :visible.sync="drawerVisible"
      :with-header="false"
      size="720px"
      class="admin-drawer"
    >
      <div class="drawer-body" v-loading="eventsLoading">
        <div class="drawer-head">
          <div>
            <h3>{{ eventUser ? (eventUser.nickname || '未设置昵称') : '' }}</h3>
            <p v-if="eventUser">
              #{{ eventUser.userId }} · {{ eventUser.schoolName }} ·
              展示 {{ num(eventUser.impression) }} · 完播 {{ num(eventUser.complete) }} ·
              可疑 {{ num(eventUser.suspect) }}
            </p>
          </div>
          <button type="button" class="drawer-close" @click="drawerVisible = false">
            <i class="el-icon-close" />
          </button>
        </div>

        <div v-if="!events.length" class="ad-empty">
          <i class="el-icon-time" />
          <p>没有观看记录</p>
        </div>
        <div v-else class="event-list">
          <div class="event" v-for="item in events" :key="String(item.id)">
            <span class="event__pill" :class="'s-' + item.status">{{ item.statusLabel }}</span>
            <div class="event__body">
              <div class="event__line">
                <span class="event__scene">{{ item.sceneLabel }}</span>
                <span class="event__time">{{ formatTime(item.createdAt) }}</span>
              </div>
              <div class="event__meta">
                <span v-if="item.durationMs">时长 {{ (item.durationMs / 1000).toFixed(1) }}s</span>
                <span v-else-if="item.legacy === 1">旧版上报（无时长）</span>
                <span v-else>无时长</span>
                <span v-if="item.errCode">错误码 {{ item.errCode }}{{ item.errMsg ? ' · ' + item.errMsg : '' }}</span>
                <span>{{ item.clientIp || '未知 IP' }}</span>
                <span v-if="item.platform">{{ item.platform }}{{ item.sdkVersion ? ' · ' + item.sdkVersion : '' }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import { getAdOverview, getAdSchools, getAdUsers, getAdUserEvents } from '@/api/ad-stats'
import { getSchoolOptions } from '@/api/school'

export default {
  name: 'AdAnalytics',
  data() {
    return {
      loading: false,
      query: {
        days: 30,
        schoolId: ''
      },
      overview: {},
      schools: [],
      schoolTotal: 0,
      schoolOptions: [],
      scopeLabel: '全平台',
      // L2：当前下钻的学校；null = 停在分校视图
      picked: null,
      userQuery: {
        keyword: '',
        sort: 'complete',
        page: 1,
        size: 20
      },
      users: [],
      userTotal: 0,
      sortOptions: [
        { label: '完播数', value: 'complete' },
        { label: '展示数', value: 'impression' },
        { label: '完播率', value: 'rate' },
        { label: '最近观看', value: 'last' }
      ],
      // L3
      drawerVisible: false,
      eventUser: null,
      events: [],
      eventsLoading: false,
      chartInstance: null
    }
  },
  computed: {
    /** 四格：只放"看整体"的数，明细都在下面的表里 */
    primaryMetrics() {
      const o = this.overview
      return [
        {
          label: '展示次数',
          value: this.num(o.impression),
          sub: `完播 ${this.num(o.complete)} + 未看完 ${this.num(o.closedEarly)} + 可疑 ${this.num(o.suspect)}`
        },
        {
          label: '完播次数',
          value: this.num(o.complete),
          sub: o.legacyCount > 0
            ? `含旧版上报 ${this.num(o.legacyCount)} 次（无时长，不可验证）`
            : '全部来自新版埋点'
        },
        {
          label: '未看完',
          value: this.num(o.closedEarly),
          sub: o.impression > 0
            ? `占展示 ${this.percentText(o.closedEarly / o.impression)}`
            : '暂无数据',
          accent: true
        },
        {
          label: '完播率',
          value: this.percentText(o.completeRate),
          sub: `涉及 ${this.num(o.userCount)} 名用户 · 分母不含加载失败`
        }
      ]
    },
    byScene() {
      return this.overview.byScene || []
    },
    errorTop() {
      return this.overview.errorTop || []
    },
    trend() {
      return this.overview.trend || []
    }
  },
  created() {
    this.loadSchoolOptions()
    this.fetchAll()
  },
  mounted() {
    window.addEventListener('resize', this.resizeChart)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resizeChart)
    if (this.chartInstance) {
      this.chartInstance.dispose()
      this.chartInstance = null
    }
  },
  methods: {
    loadSchoolOptions() {
      getSchoolOptions().then(res => {
        this.schoolOptions = (res && res.data) || []
      }).catch(() => {})
    },

    /** 页头筛选变化：同时决定"要不要展示用户明细" */
    onSchoolFilterChange() {
      if (this.query.schoolId === '' || this.query.schoolId === null) {
        this.picked = null
        this.scopeLabel = '全平台'
      } else {
        const school = this.schoolOptions.find(item => String(item.id) === String(this.query.schoolId))
        this.picked = {
          id: this.query.schoolId,
          name: school ? school.name : '指定学校'
        }
        this.scopeLabel = this.picked.name
        this.resetUserPaging()
      }
      this.fetchAll()
    },

    switchDays(days) {
      if (this.query.days === days) {
        return
      }
      this.query.days = days
      this.resetUserPaging()
      this.fetchAll()
    },

    fetchAll() {
      this.loading = true
      const params = { days: this.query.days }
      if (this.query.schoolId !== '' && this.query.schoolId !== null) {
        params.schoolId = this.query.schoolId
      }

      const tasks = [
        this.fetchOverview(params),
        this.fetchSchools()
      ]
      if (this.picked) {
        tasks.push(this.fetchUsers())
      }
      Promise.all(tasks).then(() => {
        this.loading = false
      }).catch(() => {
        this.loading = false
      })
    },

    fetchOverview(params) {
      return getAdOverview(params).then(res => {
        this.overview = (res && res.data) || {}
        this.$nextTick(() => this.renderTrend())
      })
    },

    fetchSchools() {
      return getAdSchools({
        days: this.query.days,
        page: 1,
        // 学校数量级小（几十所），一次全出比翻页更实用；后端有 200 的上界保护
        size: 200
      }).then(res => {
        const data = (res && res.data) || {}
        this.schools = data.list || []
        this.schoolTotal = data.total || 0
      })
    },

    fetchUsers() {
      if (!this.picked) {
        return Promise.resolve()
      }
      return getAdUsers({
        // ⛔ 后端用 schoolId=0 表示"不限学校"，这里传 0 而不是省略参数
        schoolId: this.picked.id,
        days: this.query.days,
        keyword: this.userQuery.keyword || undefined,
        sort: this.userQuery.sort,
        page: this.userQuery.page,
        size: this.userQuery.size
      }).then(res => {
        const data = (res && res.data) || {}
        this.users = data.list || []
        this.userTotal = data.total || 0
      })
    },

    pickSchool(item) {
      this.picked = { id: item.schoolId, name: item.schoolName }
      // 页头下拉跟着走，避免"表头筛 A 校、下面看 B 校"的矛盾
      this.query.schoolId = item.schoolId
      this.scopeLabel = item.schoolName
      this.resetUserPaging()
      this.fetchAll()
    },

    backToSchools() {
      this.picked = null
      this.query.schoolId = ''
      this.scopeLabel = '全平台'
      this.users = []
      this.userTotal = 0
      this.fetchAll()
    },

    searchUsers() {
      this.resetUserPaging()
      this.fetchUsers()
    },

    switchSort(sort) {
      if (this.userQuery.sort === sort) {
        return
      }
      this.userQuery.sort = sort
      this.resetUserPaging()
      this.fetchUsers()
    },

    changeUserPage(page) {
      this.userQuery.page = page
      this.fetchUsers()
    },

    resetUserPaging() {
      this.userQuery.page = 1
    },

    openEvents(user) {
      this.eventUser = user
      this.drawerVisible = true
      this.events = []
      this.eventsLoading = true
      getAdUserEvents({ userId: user.userId, limit: 50 }).then(res => {
        this.events = (res && res.data) || []
        this.eventsLoading = false
      }).catch(() => {
        this.eventsLoading = false
      })
    },

    renderTrend() {
      const dom = this.$refs.trendChart
      if (!dom) {
        return
      }
      if (!this.chartInstance) {
        this.chartInstance = echarts.init(dom)
      }

      const list = this.trend
      const labels = list.map(item => {
        const date = item.date || ''
        return date.length >= 10 ? date.slice(5) : date
      })

      this.chartInstance.setOption({
        grid: { left: 12, right: 20, top: 18, bottom: 6, containLabel: true },
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'line', lineStyle: { color: '#E8E0E6' } }
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: labels,
          axisLine: { lineStyle: { color: '#E8E0E6' } },
          axisLabel: { color: '#8C8191', fontSize: 11 },
          axisTick: { show: false }
        },
        yAxis: {
          type: 'value',
          splitLine: { lineStyle: { color: '#F2EEF1' } },
          axisLabel: { color: '#8C8191', fontSize: 11 }
        },
        series: [
          {
            name: '展示',
            type: 'line',
            smooth: true,
            symbol: 'circle',
            symbolSize: 5,
            showSymbol: labels.length <= 32,
            data: list.map(item => item.impression || 0),
            itemStyle: { color: '#CFC6D1' },
            lineStyle: { width: 1.8, color: '#CFC6D1' }
          },
          {
            name: '完播',
            type: 'line',
            smooth: true,
            symbol: 'circle',
            symbolSize: 5,
            showSymbol: labels.length <= 32,
            data: list.map(item => item.complete || 0),
            itemStyle: { color: '#C93B57' },
            lineStyle: { width: 2.4, color: '#C93B57' },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: 'rgba(201, 59, 87, 0.16)' },
                { offset: 1, color: 'rgba(201, 59, 87, 0.01)' }
              ])
            }
          },
          {
            name: '未看完',
            type: 'line',
            smooth: true,
            symbol: 'circle',
            symbolSize: 4,
            showSymbol: labels.length <= 32,
            data: list.map(item => item.closedEarly || 0),
            itemStyle: { color: '#FF8A65' },
            lineStyle: { width: 1.6, color: '#FF8A65', type: 'dashed' }
          }
        ]
      }, true)
    },

    resizeChart() {
      if (this.chartInstance) {
        this.chartInstance.resize()
      }
    },

    /* ---------------- 格式化 ---------------- */

    num(value) {
      const n = Number(value)
      if (!isFinite(n)) {
        return '0'
      }
      return n.toLocaleString('zh-CN')
    },

    /** 后端给的比率是 0~1 的小数，展示成 74.0% */
    percentText(rate) {
      const n = Number(rate)
      if (!isFinite(n) || n <= 0) {
        return '0%'
      }
      return (n * 100).toFixed(1) + '%'
    },

    barWidth(rate) {
      const n = Number(rate)
      if (!isFinite(n) || n <= 0) {
        return '0%'
      }
      // 最小 3%：有数据但比例极小时也得看得见，否则像"没有"
      return Math.max(n * 100, 3) + '%'
    },

    /** LocalDateTime 默认是 ISO（2026-10-18T21:40:12），统一成 10-18 21:40 */
    formatTime(value) {
      if (!value) {
        return '—'
      }
      const text = String(value).replace('T', ' ')
      return text.length >= 16 ? text.slice(5, 16) : text
    },

    /** 头像退化到首字：管理端不引头像工具，一个字足够认人 */
    avatarText(item) {
      const name = item.nickname || ''
      return name ? name.trim().charAt(0) : '·'
    }
  }
}
</script>

<style lang="scss" scoped>
/* ============================================================
   说明：hero / card / table 这套骨架与 dashboard 视觉一致，
   但 dashboard 是 scoped 定义、无法跨组件复用。
   这里以 ad- 前缀各写一份，避免动 dashboard 引入回归；
   后续若做「统计页公共组件」时再统一迁移。
   ============================================================ */

.scope-select {
  width: 200px;
}

.only-super {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 8px;
  padding: 0 6px;
  height: 19px;
  border-radius: 4px;
  background: var(--rose-50);
  color: var(--rose-600);
  border: 1px solid #f6d6da;
  font-size: 11px;

  i {
    font-size: 10px;
  }
}

/* ---------------- 深色主指标区（本屏唯一深色块） ---------------- */
.hero {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1px;
  background:
    radial-gradient(600px 260px at 88% -30%, rgba(225, 77, 104, 0.28) 0%, transparent 62%),
    linear-gradient(135deg, #241c28 0%, #171219 100%);
  border-radius: var(--r-lg);
  overflow: hidden;
  margin-bottom: 16px;
  box-shadow: 0 10px 30px -12px rgba(23, 18, 25, 0.42);

  &__item {
    position: relative;
    padding: 24px 24px 22px;
    border-right: 1px solid rgba(255, 255, 255, 0.07);

    &:last-child {
      border-right: none;
    }
  }

  &__label {
    font-size: 13px;
    color: rgba(255, 255, 255, 0.6);
    letter-spacing: 1px;
    margin-bottom: 10px;
  }

  &__value {
    font-size: 34px;
    font-weight: 600;
    color: #fff;
    line-height: 1.1;
    font-variant-numeric: tabular-nums;
  }

  &__unit {
    font-size: 14px;
    font-weight: 400;
    color: rgba(255, 255, 255, 0.5);
    margin-left: 6px;
  }

  &__sub {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.42);
    margin-top: 10px;
    line-height: 1.5;

    &.is-accent {
      color: #ffb0be;
    }
  }

  &__index {
    position: absolute;
    top: 20px;
    right: 22px;
    font-size: 12px;
    color: rgba(255, 255, 255, 0.16);
    font-variant-numeric: tabular-nums;
  }
}

/* ---------------- 卡片 ---------------- */
.ad-card {
  background: var(--card);
  border: 1px solid var(--line);
  border-radius: var(--r-lg);
  box-shadow: var(--sh-card);
  margin-bottom: 14px;
  overflow: hidden;

  &__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 15px 20px;
    border-bottom: 1px solid var(--ink-100);
  }

  &__title {
    font-size: 14px;
    font-weight: 600;
    color: var(--ink-900);
  }

  &__hint {
    font-size: 12px;
    color: var(--ink-400);
  }
}

.ad-cols {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}

.ad-chart {
  height: 240px;
  padding: 8px 8px 2px;
}

.ad-legend {
  display: flex;
  gap: 14px;

  span {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 12px;
    color: var(--ink-500);
  }

  i {
    width: 12px;
    height: 3px;
    border-radius: 2px;
  }
}

/* ---------------- 空态：图标 + 说明 ---------------- */
.ad-empty {
  padding: 40px 20px 44px;
  text-align: center;

  i {
    font-size: 28px;
    color: var(--ink-300);
  }

  p {
    margin-top: 10px;
    font-size: 13px;
    color: var(--ink-400);
  }
}

/* ---------------- 分场景 ---------------- */
.scene-list {
  padding: 6px 20px 18px;
}

.scene {
  padding: 14px 0;

  & + .scene {
    border-top: 1px dashed var(--ink-100);
  }

  &__head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    margin-bottom: 9px;
  }

  &__name {
    font-size: 13px;
    color: var(--ink-900);
    font-weight: 500;
  }

  &__rate {
    font-size: 16px;
    font-weight: 600;
    color: var(--rose-600);
    font-variant-numeric: tabular-nums;
  }

  &__bar {
    height: 6px;
    background: var(--ink-100);
    border-radius: 3px;
    overflow: hidden;
  }

  &__bar-in {
    height: 100%;
    border-radius: 3px;
    background: var(--rose-500);
    transition: width 0.3s;
  }

  &__foot {
    display: flex;
    gap: 16px;
    margin-top: 8px;

    span {
      font-size: 12px;
      color: var(--ink-400);
      font-variant-numeric: tabular-nums;
    }
  }
}

/* ---------------- 异常码 ---------------- */
.err-list {
  padding: 4px 20px 16px;
}

.err {
  display: grid;
  grid-template-columns: 58px 1fr 56px;
  gap: 12px;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px dashed var(--ink-100);

  &__code {
    font-size: 12px;
    color: var(--ink-600);
    background: var(--ink-100);
    border-radius: 4px;
    text-align: center;
    padding: 3px 0;
    font-variant-numeric: tabular-nums;
  }

  &__msg {
    font-size: 12px;
    color: var(--ink-500);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__cnt {
    font-size: 13px;
    color: var(--ink-900);
    text-align: right;
    font-variant-numeric: tabular-nums;
  }

  &__tip {
    margin-top: 12px;
    font-size: 11px;
    color: var(--ink-400);
    line-height: 1.7;
  }
}

/* ---------------- 表格 ---------------- */
.ad-table {
  width: 100%;
  border-collapse: collapse;

  th {
    padding: 10px 20px;
    font-size: 12px;
    font-weight: 500;
    color: var(--ink-400);
    text-align: right;
    background: #fdfbfa;
    white-space: nowrap;

    &:first-child {
      text-align: left;
    }
  }

  td {
    padding: 13px 20px;
    font-size: 13px;
    color: var(--ink-600);
    text-align: right;
    border-top: 1px solid var(--ink-100);

    &:first-child {
      text-align: left;
      color: var(--ink-900);
    }

    &.num {
      font-variant-numeric: tabular-nums;
    }

    &.time {
      font-size: 12px;
      color: var(--ink-400);
    }

    &.is-warn {
      color: var(--coral);
      font-weight: 600;
    }
  }

  tbody tr.is-clickable {
    cursor: pointer;

    &:hover td {
      background: #fdf7f5;
    }

    &:hover .cell-name {
      color: var(--rose-600);
    }
  }

  tbody tr.is-active td {
    background: var(--rose-50);
  }

  .cell-name {
    font-weight: 500;
  }
}

/* 行内占比条：本页唯一的"图形化数据"，让几十行学校一眼可比 */
.rate-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: flex-end;

  &__track {
    width: 76px;
    height: 5px;
    background: var(--ink-100);
    border-radius: 3px;
    overflow: hidden;
    flex-shrink: 0;
  }

  &__in {
    height: 100%;
    border-radius: 3px;
    background: var(--rose-500);

    &.is-low {
      background: var(--coral);
    }
  }

  &__text {
    min-width: 40px;
    text-align: right;
    font-size: 12px;
    color: var(--ink-500);
    font-variant-numeric: tabular-nums;
  }
}

/* ---------------- 用户单元 ---------------- */
.user-cell {
  display: flex;
  align-items: center;
  gap: 10px;

  &__avatar {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: var(--ink-900);
    color: #fff;
    font-size: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  &__text {
    display: flex;
    flex-direction: column;
    gap: 1px;
    min-width: 0;

    b {
      font-size: 13px;
      font-weight: 500;
      color: var(--ink-900);
      line-height: 1.25;
    }

    em {
      font-style: normal;
      font-size: 11px;
      color: var(--ink-400);
      font-variant-numeric: tabular-nums;
    }
  }
}

/* ---------------- 工具条 / 分页 / 面包屑 ---------------- */
.ad-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  border-bottom: 1px solid var(--ink-100);
  background: #fdfbfa;
}

.search-input {
  width: 240px;
}

.ad-pager {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  border-top: 1px solid var(--ink-100);

  > span {
    font-size: 12px;
    color: var(--ink-400);
  }
}

.ad-tip {
  padding: 12px 20px 16px;
  font-size: 11px;
  color: var(--ink-400);
  line-height: 1.7;

  b {
    color: var(--coral);
    font-weight: 500;
  }
}

.breadcrumb {
  display: flex;
  align-items: center;
  gap: 8px;

  &__up {
    font-size: 13px;
    font-weight: 400;
    color: var(--rose-600);
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }

  &__sep {
    font-weight: 400;
    color: var(--ink-300);
  }

  &__cur {
    color: var(--ink-500);
    font-weight: 500;
  }
}

/* ---------------- 抽屉（一屏，不设 tab） ---------------- */
.drawer-body {
  padding: 22px 24px 28px;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.drawer-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--ink-100);
  flex-shrink: 0;

  h3 {
    font-size: 17px;
    font-weight: 600;
    color: var(--ink-900);
  }

  p {
    margin-top: 6px;
    font-size: 12px;
    color: var(--ink-400);
    line-height: 1.6;
  }
}

.drawer-close {
  border: 0;
  background: transparent;
  width: 28px;
  height: 28px;
  border-radius: var(--r-sm);
  color: var(--ink-400);
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    background: var(--ink-100);
    color: var(--ink-900);
  }
}

.event-list {
  margin-top: 4px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

.event {
  display: flex;
  gap: 12px;
  padding: 13px 0;
  border-bottom: 1px solid var(--ink-100);

  &:last-child {
    border-bottom: none;
  }

  &__pill {
    flex-shrink: 0;
    height: 22px;
    line-height: 22px;
    padding: 0 8px;
    border-radius: 4px;
    font-size: 11px;
    background: var(--ink-100);
    color: var(--ink-500);

    /* 只给"看完"上主色，其余用中性色 —— 状态色不参与装饰 */
    &.s-COMPLETED {
      background: var(--ok-bg);
      color: var(--ok-text);
    }

    &.s-CLOSED_EARLY {
      background: var(--ink-100);
      color: var(--ink-500);
    }

    &.s-SUSPECT {
      background: var(--warn-bg);
      color: var(--warn-text);
    }

    &.s-LOAD_FAILED,
    &.s-EXPIRED {
      background: var(--danger-bg);
      color: var(--danger);
    }
  }

  &__body {
    flex: 1;
    min-width: 0;
  }

  &__line {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
  }

  &__scene {
    font-size: 13px;
    color: var(--ink-900);
    font-weight: 500;
  }

  &__time {
    font-size: 12px;
    color: var(--ink-400);
    font-variant-numeric: tabular-nums;
  }

  &__meta {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-top: 5px;

    span {
      font-size: 11px;
      color: var(--ink-400);
      font-variant-numeric: tabular-nums;
    }
  }
}

@media (max-width: 1100px) {
  .ad-cols {
    grid-template-columns: 1fr;
  }

  .hero {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
